import React, { useEffect, useState } from "react";
import "./home.css";
import "../../fonts.css";
import user from "../../models/user";

const Home = () => {
	const [themePreference, setThemePreference] = useState(() => {
		try {
			const saved = localStorage.getItem("theme");
			return saved === "dark" || saved === "light" ? saved : "system";
		} catch {
			return "system";
		}
	});
	const [systemDark, setSystemDark] = useState(
		() => window.matchMedia("(prefers-color-scheme: dark)").matches,
	);
	const darkMode = themePreference === "system" ? systemDark : themePreference === "dark";

	useEffect(() => {
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = (event) => setSystemDark(event.matches);
		media.addEventListener("change", onChange);
		return () => media.removeEventListener("change", onChange);
	}, []);

	useEffect(() => {
		document.body.classList.toggle("theme-dark", darkMode);
		document.body.classList.toggle("theme-light", !darkMode);
		return () => document.body.classList.remove("theme-dark", "theme-light");
	}, [darkMode]);

	const toggleTheme = () => {
		const preference = darkMode ? "light" : "dark";
		setThemePreference(preference);
		try {
			localStorage.setItem("theme", preference);
		} catch {
			// Keep the selected theme usable when storage is unavailable.
		}
	};

	return (
		<div className={darkMode ? "theme-dark" : "theme-light"}>

			<div className="window">
				<div className="terminal-bar">
					<div className="terminal-dots">
						<span></span>
						<span></span>
						<span></span>
					</div>

					<div className="terminal-title">pedro@localhost — portfolio</div>

					<button
						className="theme-toggle"
						type="button"
						role="switch"
						aria-label="Dark mode"
						aria-checked={darkMode}
						onClick={toggleTheme}
					>
						<span className="theme-toggle-track" aria-hidden="true">
							<span className="theme-toggle-thumb" />
						</span>
					</button>
				</div>

				<main className="terminal-content">
					<h1>{user.name}</h1>
					<p className="tagline typing">
						Backend Systems • Enterprise Architecture • AI-Driven Automation
					</p>

					<p>
						I design and scale enterprise backend systems that power high-stakes
						platforms. My focus is building resilient, secure, and long-term
						maintainable architectures.
					</p>

					<p>
						Based in Los Angeles. 10+ years building production systems across
						enterprises, e-commerce infrastructure, and high-availability
						environments.
					</p>

					<div className="section">
						<h2>Enterprise Experience</h2>

						<p>
							I currently design and evolve scalable .NET microservices
							supporting enterprise platforms at Robert Half.
						</p>

						<ul>
							<li>
								Led end-to-end implementation of executive compensation systems
								serving multiple enterprise business units
							</li>
							<li>
								Strengthened platform security, reliability, and deployment
								architecture
							</li>
							<li>
								Designed AI-powered internal systems using Azure OpenAI to
								augment operational workflows
							</li>
							<li>
								Collaborated on a custom Model Context Protocol (MCP) server
								that enables different projects to expose their own tools to
								AI agents
							</li>
							<li>
								Developed AI agents to automate manual tasks and operational
								workflows
							</li>
							<li>
								Modernized legacy APIs to .NET 10, improving maintainability and
								long-term scalability
							</li>
							<li>
								Developed Azure Functions supporting compliance and automation
								workflows
							</li>
						</ul>
					</div>

					<div className="section">
						<h2>Selected Impact</h2>

						<p>
							Architected and led development of a custom ERP platform
							integrating 7+ e-commerce and logistics systems, automating order
							tracking, inventory synchronization, and financial workflows.
						</p>

						<p>
							Contributed to full-stack solutions supporting the Rio 2016
							Olympic Games digital platform, maintaining 100% uptime during
							globally broadcast live events under extreme traffic conditions.
						</p>
					</div>

					<div className="section">
						<h2>Independent Projects</h2>
						<p>
							Outside of enterprise work, I build products and utilities for
							business owners and developers.
						</p>
						<ul>
							<li>
								<strong>Bearer My Buddy</strong> — Chrome extension that
								automatically detects, validates, organizes and one-click copies
								bearer tokens from network requests. Saves time during API
								testing/debugging.
								<br />
								<a
									href="https://chromewebstore.google.com/detail/gjcoanlgidogpaidfidjfhjilgllombi?utm_source=bearermybuddy&utm_medium=landing-page&utm_campaign=launch-promo&utm_content=hero-cta"
									target="_blank"
									rel="noopener noreferrer"
									className="terminal-link"
								>
									→ bearermybuddy.pedrohma.com
								</a>
								<br />
								<small>(Available on the Chrome Web Store)</small>
							</li>
							<li>
								<strong>Metric Pilot</strong> — I built a platform that helps
								business owners understand customer feedback from Google reviews
								through AI-powered monthly reputation reports.
								<br />
								<a
									href="https://metricpilotapp.com/?utm_source=pedrohma.com&utm_medium=referral&utm_campaign=portfolio"
									target="_blank"
									rel="noopener noreferrer"
									className="terminal-link"
								>
									→ metricpilotapp.com
								</a>
							</li>
						</ul>
					</div>

					<div className="section">
						<h2>Core Capabilities</h2>

						<ul>
							<li>
								Designing and scaling distributed microservices architectures
								(.NET, REST APIs)
							</li>
							<li>
								Building secure authentication and role-based access control
								systems
							</li>
							<li>Cloud infrastructure design across Azure and AWS</li>
							<li>
								Developing AI-powered enterprise tooling and internal automation
								systems
							</li>
							<li>
								System performance optimization and reliability engineering
							</li>
							<li>
								Cross-functional collaboration translating business requirements
								into scalable technical solutions
							</li>
						</ul>
					</div>

					<div className="section">
						<h2>Engineering Philosophy</h2>

						<p>
							I approach engineering with a systems mindset. I prioritize
							clarity, security, and long-term maintainability from the start.
							My goal is not just delivering features, but designing foundations
							that scale with the business.
						</p>

						<ul>
							<li>Design before implementation</li>
							<li>Clarity over complexity</li>
							<li>Security-first thinking</li>
							<li>Long-term maintainability over short-term shortcuts</li>
							<li>Raising engineering standards through mentorship</li>
						</ul>
					</div>

					{/* <div
						className="footer"
						style={{ marginTop: "3rem", opacity: 0.6, fontSize: "0.9rem" }}
					>
						pedro@localhost • {new Date().getFullYear()}
					</div> */}

					<div className="section">
						<h2>Contact</h2>

						<div className="contact-links">
							{user.links.map((link, index) => (
								<a
									key={index}
									href={link.url}
									target="_blank"
									rel="noopener noreferrer"
									className="terminal-link"
								>
									{`[ ${link.name} ]`}
								</a>
							))}
						</div>
					</div>
				</main>
			</div>
		</div>
	);
};

export default Home;
